import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sail_dx_token";

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export const setToken = (t) =>
  t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);
export const getToken = () => localStorage.getItem(TOKEN_KEY);

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong.";
}

export const PROGRESS = {
  on_track: { label: "On Track", color: "hsl(var(--healthy))" },
  needs_attention: { label: "Needs Attention", color: "hsl(var(--watch))" },
  delayed: { label: "Delayed", color: "hsl(var(--at-risk))" },
};

export const STAGE_COLORS = {
  Completed: "hsl(var(--healthy))",
  "Completed & Running": "hsl(var(--healthy))",
  "Completed & Discontinued": "hsl(25 5% 45%)",
  Ongoing: "hsl(var(--primary))",
  Identified: "hsl(var(--watch))",
  "Yet to Start": "hsl(var(--watch))",
  "Put on Hold": "hsl(25 5% 45%)",
  Dropped: "hsl(0 0% 55%)",
  "Short Term": "hsl(var(--watch))",
  "Long Term": "hsl(25 5% 45%)",
  "Task Force Formed": "hsl(240 5% 65%)",
};

export const TERM_COLORS = {
  "Short-Term": "hsl(var(--primary))",
  "Long-Term": "hsl(199 89% 40%)",
};

export const JOURNEY_COLORS = {
  "In-Pipeline": "hsl(240 5% 65%)",
  "Data Collection / EDA": "hsl(199 89% 48%)",
  "Model Development": "hsl(199 89% 40%)",
  "Cold Trials": "hsl(var(--watch))",
  "Baseline Defined": "hsl(36 92% 50%)",
  "Baseline Signed Off": "hsl(36 92% 40%)",
  "Hot Trials": "hsl(var(--primary))",
  "Go-Live": "hsl(160 84% 39%)",
  "Assessment Period Started": "hsl(170 70% 34%)",
  "Assessment Period Completed": "hsl(158 64% 30%)",
  "Final Signoff & Handover": "hsl(150 60% 32%)",
  Completed: "hsl(var(--healthy))",
};

export const STAGE_STATE_COLORS = {
  completed: "hsl(var(--healthy))",
  running: "hsl(var(--primary))",
  reached: "hsl(199 89% 40%)",
  expected: "hsl(var(--watch))",
  passed: "hsl(25 5% 45%)",
  pending: "hsl(240 5% 60%)",
};

export const TIMELINE_LABELS = {
  on_track: "Running to time",
  slipping: "Slipping",
  overdue: "Overshooting",
  completed: "Completed",
  paused: "On hold or dropped",
  no_dates: "Dates awaited",
};

export const TIMELINE_COLORS = {
  completed: "hsl(var(--healthy))",
  on_track: "hsl(var(--primary))",
  due_soon: "hsl(var(--watch))",
  slipping: "hsl(25 90% 48%)",
  overdue: "hsl(var(--at-risk))",
  not_planned: "hsl(240 5% 60%)",
  paused: "hsl(25 5% 45%)",
  no_dates: "hsl(240 5% 60%)",
};

export const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);

// A figure of 0 is an answer the unit gave, so it reads ₹0.00 Cr — only a blank reads "—".
export const fmtCr = (v) =>
  v === null || v === undefined || v === "" || Number.isNaN(Number(v))
    ? "—"
    : `₹${Number(v).toFixed(2)} Cr`;
export const fmtLakhValue = (v) => (v ? `₹${Number(v).toLocaleString("en-IN")} L` : "—");

export function fmtDay(value) {
  if (!value) return "—";
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export async function downloadFile(path, params, filename) {
  let res;
  try {
    res = await api.get(path, { params, responseType: "blob" });
  } catch (e) {
    // A refusal on a download arrives as a Blob, so the server's own words would otherwise be
    // lost behind "Request failed with status code 400". Read them out and carry them along.
    const body = e?.response?.data;
    if (body instanceof Blob) {
      try {
        const said = JSON.parse(await body.text());
        e.response.data = said;
      } catch {
        /* not JSON — leave the error as it came */
      }
    }
    throw e;
  }
  // The server names the file (e.g. "BSL Dx Project Status as on 12.06.2026.xlsx"); honour it.
  const sent = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(
    res.headers?.["content-disposition"] || "",
  );
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = sent ? decodeURIComponent(sent[1]) : filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
